import { apiSlice } from "./apiSlice";
import { MapSavedQuery } from "../mappers/SaveQuery";
import { mapWizardConfig } from "../mappers/DataWranglerWizard";

const dataWranglerTransform = (responseData: any) => {
    const mappedData: any[] = [];
    let total = 0;
    Object.keys(responseData || {}).forEach((cohort) => {
        const cohortData = responseData[cohort];
        const mapCohort = {
            name: cohort,
            sites: cohortData['sites'],
            sexSummary: cohortData.sexSummary,
            total: Object.values(cohortData.sexSummary).reduce((sum: number, v: any) => sum + v, 0)
        };
        mappedData.push(mapCohort);
        total += mapCohort.total
    });

    return {
        data: mappedData,
        total: total
    }
}

export const dataWranglerApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        dashboard: builder.query({
            query: () => '/data_wrangler',
            providesTags: (result, error, { database }) => [
                { type: 'DW_Dashboard', id: 'dashboard' },
                { type: 'Database', id: database },
            ],
            transformResponse: dataWranglerTransform
        }),
        summary: builder.mutation({
            query: visits => {
                return {
                    url: '/data_wrangler',
                    method: 'POST',
                    body: { ...visits }
                }
            },
            transformResponse: dataWranglerTransform,
            invalidatesTags: [{ type: 'DataWrangler', id: 'SUMMARY' }],
        }),
        wizard: builder.query({
            query: () => "/data_wrangler/wizard",
            transformResponse: (responseData: any) => mapWizardConfig(responseData),
            providesTags: (result, error, { database }) => [
                { type: 'DW_Dashboard', id: 'config' },
                { type: 'Database', id: database },
            ]
        }),
        downloadQueueQuery: builder.mutation({
            query: body => {
                return {
                    url: `download-client/enqueue`,
                    responseHandler: (response: any) => {
                        return response?.status;
                    },
                    method: 'POST',
                    body: { ...body }
                }
            },

        }),
        saveQuery: builder.mutation({
            query: query => {
                return {
                    url: '/data_wrangler/save_query',
                    method: 'POST',
                    body: { ...query }
                }
            },
            invalidatesTags: [{ type: 'DW_Dashboard', id: 'savedQueries' }]
        }),
        getSavedQueries: builder.query({
            query: () => `/data_wrangler/save_query`,
            transformResponse: (responseData: any) => {
                return [
                    {
                        id: -1,
                        name: 'All Data',
                        description: 'This graph shows the number of participants in the database, and can be adjusted to show a breakdown of the study’s choice',
                        builder: 'wizard',
                        type: 'core',
                        user: 'admin',
                        query: {
                            ageRange: [],
                            constructs: [],
                            instruments: []
                        }
                    },
                    ...responseData.map((beQuery: any) => MapSavedQuery(beQuery))
                ];
            },
            providesTags: (result, error, { database }) => [
                { type: 'DW_Dashboard', id: 'savedQueries' },
                { type: 'Database', id: database },
            ]
        }),
        getSharedQueries: builder.query({
            query: () => `/data_wrangler/shared_query`,
            transformResponse: (responseData: any) => {
                return responseData.map((beQuery: any) => MapSavedQuery(beQuery))
            },
            providesTags: (result, error, { database }) => [
                { type: 'DW_Dashboard' },
                { type: 'Database', id: database },
            ]
        }),
        saveSharedQuery: builder.mutation({
            query: body => {
                return {
                    url: '/data_wrangler/shared_query',
                    method: 'POST',
                    body
                }
            },
            invalidatesTags: [{ type: 'DW_Dashboard' }]
        }),
        getSavedAdvancedQueries: builder.query({
            query: () => `/data_wrangler/save_query`,
            transformResponse: (responseData: any) => {
                return [
                    ...responseData.map((beQuery: any) => MapSavedQuery(beQuery))
                ];
            },
            providesTags: (result, error, { database }) => [
                { type: 'DW_Dashboard', id: 'savedQueries' },
                { type: 'Database', id: database },
            ]
        }),
        transferFiles: builder.mutation({
            query: body => {
                return {
                    url: '/data_wrangler/transfer',
                    method: 'POST',
                    body: { ...body }
                }
            }
        }),
        transferFilesSummary: builder.mutation({
            query: body => {
                return {
                    url: '/data_wrangler/transfer/summary',
                    method: 'POST',
                    body: { ...body }
                }
            }
        }),
        adwTransferFilesSummary: builder.mutation({
            query: body => {
                return {
                    url: `/data_wrangler/adw_transfer/summary`,
                    method: 'POST',
                    body: { ...body }
                }
            }
        }),
        adwTransferFiles: builder.mutation({
            query: body => {
                const params = new URLSearchParams();
                params.append('page', body.page);
                params.append('type', body.type);
                params.append('limit', body.limit);
                body.participantLimit && params.append('participantLimit', body.participantLimit);
                return {
                    url: `/data_wrangler/adw_transfer?${params.toString()}`,
                    method: 'POST',
                    body: {
                        ...body.selected,
                        summary: body.page === 0 ? body.summary : ""
                    }
                }
            }
        }),
        participantCheck: builder.mutation({
            query: participants => {
                return {
                    url: '/data_wrangler/check_participants',
                    method: 'POST',
                    body: participants
                }
            }
        })
    })
})

export const {
    useDashboardQuery,
    useWizardQuery,
    useSaveQueryMutation,
    useSaveSharedQueryMutation,
    useGetSharedQueriesQuery,
    useGetSavedAdvancedQueriesQuery,
    useGetSavedQueriesQuery,
    useAdwTransferFilesMutation,
    useParticipantCheckMutation
} = dataWranglerApiSlice;